import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Avatar, Box, Stack, Typography } from '@mui/material';

import { useQueryClient } from '@tanstack/react-query';
import { useModal } from 'mui-modal-provider';

import { venueActionAccessRequestApi } from '../../../api/accessRequest/venueActionAccessRequestApi';
import {
  UserAccessRequestResultValue,
  VennueAccessRequestItem,
  VenueAccessREnum,
} from '../../../graphql/API';
import useAccessRequestQuery from '../../../hooks/reactQuery/useAccessRequestQuery';
import { extractSubscriptionPlan } from '../../../pages/SubscriptionPlans/common/helper';
import { useAppSelector } from '../../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../../utils/venue-tags';
import LoadingButton from '../../Button/LoadingButton';
import BaseDialog, { BaseDialogProps } from '../Base';
import { BrandLimitReachedPopup } from '../BrandLimitReached/BrandLimitReachedPopup';

type Props = {
  accessRequest: VennueAccessRequestItem | null;
} & BaseDialogProps;

export const ProductRequestsRejectedDialog: React.FC<Props> = ({ accessRequest, ...rest }) => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { showModal } = useModal();

  const venueState = useAppSelector((state) => state.venue.value);
  const { subscription } = useAppSelector((state) => state.subscription.value);
  const isBrand = venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans) || false;

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const [isUpdatingStarted, setIsUpdatingStarted] = React.useState(false);
  const [isBulkUpdatingStarted, setIsBulkUpdatingStarted] = React.useState(false);

  const { data, isFetching, isLoading } = useAccessRequestQuery(
    {
      status: UserAccessRequestResultValue.requested,
      userID: Number(accessRequest?.user?.id),
    },
    (accessRequest?.totalRequested || 0) >= 2 && rest.open,
    9999,
  );

  const onRejectSingleRequestHandle = async (accessRequest: VennueAccessRequestItem) => {
    if (isBrand && !subscriptionPlan) {
      return navigate('/plans');
    }
    try {
      if (accessRequest.userID && accessRequest.offerID) {
        setIsUpdatingStarted(true);
        await venueActionAccessRequestApi({
          action: VenueAccessREnum.declined,
          userID: accessRequest.userID,
          offerID: accessRequest.offerID,
        });

        setIsUpdatingStarted(false);
        rest?.onClose?.({}, 'backdropClick');
        await queryClient.invalidateQueries({ queryKey: ['list-access-request'] });
      }
    } catch (error) {
      const _error = error as Error;
      if (_error.message.includes('limit')) {
        showModal(BrandLimitReachedPopup, {});
      }
      setIsUpdatingStarted(false);
      console.error('Error performing update action:', error);
    }
  };

  const bulkRejectProductRequestAction = async (productRequests: VennueAccessRequestItem[]) => {
    if (isBrand && !subscriptionPlan) {
      return navigate('/plans');
    }
    try {
      setIsBulkUpdatingStarted(true);

      const filteredUserRequests = productRequests.filter(
        (request) => request.userID === accessRequest?.userID,
      );

      if (filteredUserRequests.length > 0) {
        await Promise.all(
          filteredUserRequests.map((item) => {
            if (!item.userID || !item.offerID) {
              return;
            }
            return venueActionAccessRequestApi({
              action: VenueAccessREnum.declined,
              userID: item.userID,
              offerID: item.offerID,
            });
          }),
        );
      }
      await queryClient.invalidateQueries({ queryKey: ['list-access-request'] });
      await queryClient.invalidateQueries({ queryKey: ['venueListAccessRequestsCountAPI'] });
      setIsBulkUpdatingStarted(false);
      rest.onClose?.({}, 'escapeKeyDown');
    } catch (error) {
      const _error = error as Error;
      if (_error.message.includes('limit')) {
        showModal(BrandLimitReachedPopup, {});
      }
      setIsBulkUpdatingStarted(false);
      console.error('Error performing bulk action:', error);
    }
  };

  return (
    <BaseDialog width='480px' minWidth='480px' height={'630px'} minHeight='630px' {...rest}>
      <Stack direction={'column'} spacing={6}>
        <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
          <Avatar
            variant='rounded'
            src={String(accessRequest?.user?.picture?.medium)}
            alt={accessRequest?.user?.firstname || 'avatar'}
            sx={{ width: '226px', height: '226px', objectFit: 'contain', borderRadius: '50%' }}
          />
        </Box>

        <Box
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          flexDirection={'column'}
          gap={'12px'}
        >
          <Typography fontSize={'20px'} fontWeight={500} fontFamily={'Poppins'}>
            Do you want to reject all her requests?
          </Typography>
          <Typography fontSize={'15px'} fontFamily={'Poppins'} textAlign={'center'} px={3}>
            {accessRequest?.user?.firstname} requested {accessRequest?.totalRequested} more
            products, do you want to reject all of them or just the request for the{' '}
            {accessRequest?.offer?.title}?
          </Typography>
        </Box>

        <Box display={'flex'} flexDirection={'column'} gap={'15px'}>
          <LoadingButton
            variant='outlined'
            color='primary'
            sx={{ padding: '10px 20px' }}
            fullWidth
            onClick={() => {
              if (accessRequest) onRejectSingleRequestHandle(accessRequest);
            }}
            loading={isUpdatingStarted}
            disabled={isUpdatingStarted}
          >
            Reject only this request
          </LoadingButton>
          <LoadingButton
            variant='outlined'
            color='error'
            sx={{ padding: '10px 20px' }}
            disabled={isLoading || isFetching || isBulkUpdatingStarted}
            loading={isBulkUpdatingStarted}
            onClick={() => {
              bulkRejectProductRequestAction(data);
            }}
            fullWidth
          >
            Reject all requests ({accessRequest?.totalRequested})
          </LoadingButton>
        </Box>
      </Stack>
    </BaseDialog>
  );
};
